import type * as React from 'react';
import { Notification } from '@/modules/Notification';
import type { NotificationIntent } from '@/modules/Notification';
import { cn } from '@/shared/utils/cn';
import type { NotificationProps } from '@/shared/types/message';
import {
  mergeNotificationCard,
  messageQueueEntryToNotification,
  resolveNotificationClickHandler,
} from './notificationItemCardMapping';
import {
  STACK_EXPAND_EASE,
  STACK_LAYER_COLLAPSED_TRIM_PER_LEVEL_PX,
} from './notificationItemStackLayout';

/** Props de la vista apilada: capas bajo la frontal + frontal y pie del mazo. */
export interface NotificationItemStackDeckProps {
  /** Notificaciones que se pintan como capas absolutas bajo la frontal. */
  stackUnderLayers: NotificationProps[];
  /** Tarjeta visible al frente del stack. */
  frontNotification: NotificationProps;
  /** Clave estable de React para la frontal (id preferido). */
  frontNotificationKey: number | undefined;
  /** Píxeles de separación vertical entre capas (colapsado vs expandido). */
  perLayerBottomPx: number;
  /** Si el hover expande el stack a ancho completo. */
  isStackExpanded: boolean;
  /** Intención del mazo. */
  intent: NotificationIntent;
  /** Animación de salida del grupo. */
  isLeaving: boolean;
  /** Clases aplicadas al `Alert` del deck (p. ej. anular transiciones en multilayer). */
  stackDeckAlertClassName: string;
  /** Duración efectiva de barra solo para la id de la notificación más reciente del mazo. */
  timerDurationInStack: (id: number | undefined) => number | undefined;
  /** Wrapper de clases para la barra según hover y duración. */
  progressBarClassWhenStackHover: (durationMs: number | undefined) => string | undefined;
  /** Cierre por tarjeta. */
  handleDismissOne: (n: NotificationProps) => (e: React.MouseEvent<HTMLButtonElement>) => void;
  /** Pie del stack (“N notificaciones más”) u otro chrome. */
  stackListFooter: React.ReactNode;
}

/**
 * Vista apilada: capas inferiores absolutas + frontal relativa.
 *
 * @example
 * // Compuesto por `NotificationItem`; referencia de integración:
 * <NotificationItemStackDeck
 *   stackUnderLayers={tail}
 *   frontNotification={head}
 *   frontNotificationKey={head.id}
 *   perLayerBottomPx={8}
 *   isStackExpanded={false}
 *   intent="informativa"
 *   isLeaving={false}
 *   stackDeckAlertClassName="ui-w-full"
 *   timerDurationInStack={() => undefined}
 *   progressBarClassWhenStackHover={() => undefined}
 *   handleDismissOne={(n) => (e) => { e.stopPropagation(); }}
 *   stackListFooter={null}
 * />
 */
export function NotificationItemStackDeck({
  stackUnderLayers,
  frontNotification,
  frontNotificationKey,
  perLayerBottomPx,
  isStackExpanded,
  intent,
  isLeaving,
  stackDeckAlertClassName,
  timerDurationInStack,
  progressBarClassWhenStackHover,
  handleDismissOne,
  stackListFooter,
}: NotificationItemStackDeckProps) {
  return (
    <>
      {stackUnderLayers.map((notification, layerIdx) => {
        const stepsForBottom = stackUnderLayers.length - layerIdx;
        const depthFromFront = stepsForBottom;
        const collapsedWidthTrimPx = depthFromFront * STACK_LAYER_COLLAPSED_TRIM_PER_LEVEL_PX;
        const collapsedHorizontalInsetPx = collapsedWidthTrimPx / 2;

        return (
          <div
            key={notification.id}
            className={cn(
              'ui-absolute ui-max-w-full ui-origin-bottom',
              'ui-pointer-events-auto',
              'ui-transition-[bottom]',
              STACK_EXPAND_EASE,
              stepsForBottom >= 1 && 'ui-drop-shadow-02'
            )}
            style={{
              bottom: -stepsForBottom * perLayerBottomPx,
              zIndex: 10 + layerIdx,
              left: isStackExpanded ? 0 : collapsedHorizontalInsetPx,
              right: isStackExpanded ? 0 : collapsedHorizontalInsetPx,
            }}
            aria-hidden
          >
            <Notification
              {...mergeNotificationCard(
                messageQueueEntryToNotification(notification, intent, {
                  className: stackDeckAlertClassName,
                  isLeaving,
                  duration: timerDurationInStack(notification.id),
                  progressBarClassName: progressBarClassWhenStackHover(
                    timerDurationInStack(notification.id)
                  ),
                  animation: 'none',
                  showActionButtons: false,
                  onClose: notification.showCloseButton
                    ? handleDismissOne(notification)
                    : undefined,
                  onClick: undefined,
                }),
                notification.notificationCard
              )}
            />
          </div>
        );
      })}

      <div
        className={cn('ui-relative ui-z-50', stackUnderLayers.length > 0 && 'ui-drop-shadow-02')}
      >
        <Notification
          key={frontNotificationKey}
          {...mergeNotificationCard(
            messageQueueEntryToNotification(frontNotification, intent, {
              className: stackDeckAlertClassName,
              isLeaving,
              duration: timerDurationInStack(frontNotification.id),
              progressBarClassName: progressBarClassWhenStackHover(
                timerDurationInStack(frontNotification.id)
              ),
              animation: 'none',
              showActionButtons: true,
              footer: stackListFooter,
              onClose: frontNotification.showCloseButton
                ? handleDismissOne(frontNotification)
                : undefined,
              // Mazo colapsado (≥2): el clic abre la lista completa; onClick solo en vista lista.
              onClick:
                stackUnderLayers.length > 0
                  ? undefined
                  : resolveNotificationClickHandler(frontNotification),
            }),
            stackUnderLayers.length > 0
              ? {
                  ...frontNotification.notificationCard,
                  events: {
                    ...frontNotification.notificationCard?.events,
                    onClick: undefined,
                  },
                }
              : frontNotification.notificationCard
          )}
        />
      </div>
    </>
  );
}

NotificationItemStackDeck.displayName = 'NotificationItemStackDeck';
